'use client'

import ActivityLoading from '@/components/activity-loading'

import React from 'react'
import Link from 'next/link'
import useSWR from 'swr'
import { ArrowRight } from 'lucide-react'
import useIsHydrated from '@/utils/hooks/useIsHydrated'
import { offersEnabled } from '@/utils/offers'
import type { BidBookSource } from '@/utils/server/bid-book'
import useBidBook from '@/app/bids/use-bid-book'
import { BidActivityList } from '@/app/bids/rails'

const fetchBook = async (url: string): Promise<BidBookSource> => {
	const r = await fetch(url)
	if (!r.ok) throw new Error('Bids unavailable')
	return r.json()
}

function Stream({ source }: { source: BidBookSource }) {
	const hydrated = useIsHydrated()
	const { activity, status, retry } = useBidBook({
		collections: source.collections,
		book: source.book,
		realOffers: hydrated && offersEnabled()
	})
	return (
		<div className="flex h-full min-h-0 flex-col" data-testid="bids-feed">
			<div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-2 text-[11px] text-muted-foreground">
				<span>Live funded offers</span>
				<Link
					href="/offers"
					className="inline-flex shrink-0 items-center gap-1 font-semibold text-foreground hover:underline"
				>
					All offers <ArrowRight className="size-3" aria-hidden="true" />
				</Link>
			</div>
			{activity.length ? (
				<BidActivityList activity={activity} />
			) : status === 'loading' ? (
				<ActivityLoading />
			) : (
				<p className="px-4 py-6 text-xs text-muted-foreground" role="status">
					{status === 'error' ? 'Offers are temporarily unavailable. ' : 'No offers made yet. '}
					{status === 'error' ? (
						<button type="button" className="font-semibold text-foreground underline" onClick={retry}>
							Retry
						</button>
					) : (
						<Link href="/offers" className="font-semibold text-foreground underline">
							Make the first
						</Link>
					)}
				</p>
			)}
		</div>
	)
}

/** Most recent bids across collections, for the activity rail. */
export default function BidsFeed() {
	const { data, error, isLoading, mutate } = useSWR('/api/bids/book', fetchBook, {
		revalidateOnFocus: false,
		dedupingInterval: 60_000
	})
	if (data) return <Stream key={data.at} source={data} />
	if (isLoading) return <ActivityLoading />
	return (
		<p className="px-4 py-6 text-xs text-muted-foreground" role="status">
			{error ? 'Offers are temporarily unavailable. ' : 'No offers to show yet. '}
			<button
				type="button"
				className="font-semibold text-foreground underline"
				onClick={() => void mutate()}
			>
				Retry
			</button>
		</p>
	)
}
